import { useState, useEffect, useRef } from 'react';

const useDynamicRowSpan = (isExpanded, dependencies = []) => {
  const [rowSpan, setRowSpan] = useState(1);
  const cardRef = useRef(null);
  const [gridRowHeight, setGridRowHeight] = useState(0);

  useEffect(() => {
    if (!isExpanded) {
      setRowSpan(1);
      return;
    }

    const calculateRowSpan = () => {
      if (!cardRef.current) return;

      // Get the parent grid container
      const gridContainer = cardRef.current.parentElement;
      if (!gridContainer) return;

      // Find a non-expanded card to measure standard row height
      const nonExpandedCard = gridContainer.querySelector('[data-event-id]:not(.expanded-card)');
      if (!nonExpandedCard) return;

      // Get the computed styles
      const gridStyles = window.getComputedStyle(gridContainer);
      const gap = parseInt(gridStyles.gap) || 12; // Default gap is 12px (gap-3 = 0.75rem)
      
      // Measure the standard row height (including gap)
      const standardRowHeight = nonExpandedCard.offsetHeight + gap;
      
      // Measure the expanded card's natural height
      const expandedHeight = cardRef.current.scrollHeight;
      
      // Calculate how many rows we need
      // We subtract one gap because the last row doesn't need a gap after it
      const neededRows = Math.ceil((expandedHeight + gap) / standardRowHeight);
      
      // Set a reasonable maximum to prevent excessive spanning
      const finalRowSpan = Math.min(neededRows, 6);
      
      setRowSpan(finalRowSpan);
      setGridRowHeight(standardRowHeight);
    };

    // Calculate immediately
    calculateRowSpan();

    // Recalculate on window resize
    const handleResize = () => {
      requestAnimationFrame(calculateRowSpan);
    };

    window.addEventListener('resize', handleResize);

    // Also recalculate when content changes
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(calculateRowSpan);
    });

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [isExpanded, ...dependencies]); // eslint-disable-line react-hooks/exhaustive-deps

  return { rowSpan, cardRef, gridRowHeight };
};

export default useDynamicRowSpan;